import {Form, FormikProvider, useFormik} from 'formik';
import {find, forEach, map} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmModal, InputFormik, Select} from 'components';
import {SALESFORCE_INTEGRATION_SOURCES} from 'constants/integrations';
import {AUTH_SOURCE_TRANSLATE} from 'constants/translate';
import {EDITABLE_INTEGRATION_SOURCES} from 'constants/user';
import {AuthSource} from 'enums';
import {Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, User} from 'types';
import {apiUtils, generateUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.INTEGRATIONS;

const FIELDS = {
    ORG_ID: 'credentials.orgId',
    USER_ID: 'credentials.userId',
    NAME: 'credentials.name',
    KEY: 'credentials.key'
};

type SalesforceIntegrationCredentials = {
    orgId: string,
    userId: string
};

type AppointmentsApplicationCredentials = {
    name: string,
    key: string
};

type FormFields = {
    credentials: SalesforceIntegrationCredentials | AppointmentsApplicationCredentials;
};

type Props = {
    open: boolean;
    user?: User;
    source?: AuthSource;
    loading: boolean;
    error: ApiError;
    onClose: (confirm: boolean, data?: any) => void;
};

export const IntegrationDetailsModal: React.FC<Props> = ({open, user, source: preSelectedSource, error, onClose}) => {
    const {t} = useTranslation();
    const buildSourceOption = useCallback((source: AuthSource) => ({
        id: source,
        name: t(AUTH_SOURCE_TRANSLATE[source])
    }), [t]);

    const sourceOptions = useMemo(() => map(
        EDITABLE_INTEGRATION_SOURCES.filter((src) => preSelectedSource ?
            true :
            !find(user?.integrations, (integration) => integration.source === src)
        ),
        buildSourceOption
    ), [user, preSelectedSource, buildSourceOption]);

    const [selectedSource, setSelectedSource] = useState<Option>();

    const initialValues = useMemo(() => {
        if (open) {
            //TODO: Actually here user is profile, so we need to refactor 'user' and 'profile' synonyms
            const currentIntegration = find(user?.integrations, (integration) => integration.source === selectedSource?.id);
            if (currentIntegration) {
                return {credentials: currentIntegration.credentials};
            } else {
                if (selectedSource?.id === AuthSource.AppointmentsApplication) {
                    return ({
                        credentials: {
                            name: '',
                            key: generateUtils.generateToken()
                        }
                    });
                } else {
                    return ({
                        credentials: {
                            orgId: '',
                            userId: ''
                        }
                    });
                }
            }
        }
    }, [open, selectedSource?.id, user?.integrations]);

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        onSubmit: (formValues: FormFields) => {
            const {credentials} = formValues;
            const data = {
                source: selectedSource.id,
                credentials
            };
            onClose(true, data);
        }
    });

    const {setFieldError, submitForm, resetForm, setValues} = formik;

    const updateCredentials = useCallback((source) => {
        const existingIntegration = find(user.integrations, (integration) => integration.source === source);
        if (existingIntegration) {
            setValues({
                credentials: existingIntegration.credentials,
                source: buildSourceOption(source) as any
            });
        }
    }, [user, setValues, buildSourceOption]);

    const onSubmit = useCallback(async (confirm) => {
        const data: {source?: AuthSource, credentials?: any} = {};
        if (confirm) {
            await submitForm();
        } else {
            onClose(confirm, data);
        }
    }, [onClose, submitForm]);

    const onSourceChanged = useCallback((value) => {
        setSelectedSource(value);
        updateCredentials(value);
    }, [setSelectedSource, updateCredentials]);

    useEffect(() => {
        const formErrors = apiUtils.getFieldsValidationErrorMessages(error, t,
            [
                {field: FIELDS.NAME, overrides: {}},
                {field: FIELDS.KEY, overrides: {}},
                {field: FIELDS.USER_ID, overrides: {}},
                {field: FIELDS.ORG_ID, overrides: {}}
            ]);
        forEach(formErrors, (value, key) => value && setFieldError(key, value));
    }, [error, t, setFieldError]);

    useEffect(() => {
        if (!open && !error) {
            resetForm();
        }
    }, [open, error, resetForm]);

    useEffect(() => {
        if (open) {
            setSelectedSource(preSelectedSource ?
                find(sourceOptions, {id: preSelectedSource}) :
                sourceOptions[0]
            );
            if (preSelectedSource) {
                updateCredentials(preSelectedSource);
            }
        }
    }, [open, sourceOptions, updateCredentials, preSelectedSource, setSelectedSource]);

    return (
        <ConfirmModal
            title={t(T_KEYS.DETAILS_MODAL.TITLE)}
            maxWidth='sm'
            fullWidth
            open={open}
            onClose={onSubmit}
        >
            <Select
                placeholder={t(T_KEYS.FIELDS.SOURCE)}
                value={selectedSource}
                options={sourceOptions}
                onChange={onSourceChanged}
            />
            <FormikProvider value={formik}>
                <Form>
                    {SALESFORCE_INTEGRATION_SOURCES.includes((selectedSource as any)?.id) &&
                        <Fragment>
                            <InputFormik name={FIELDS.ORG_ID} label={t(T_KEYS.FIELDS.ORG_ID)} spacing='top' required />
                            <InputFormik name={FIELDS.USER_ID} label={t(T_KEYS.FIELDS.USER_ID)} spacing='top' required />
                        </Fragment>}
                    {selectedSource?.id === AuthSource.AppointmentsApplication &&
                        <Fragment>
                            <InputFormik name={FIELDS.NAME} label={t(T_KEYS.FIELDS.NAME)} spacing='top' required />
                            <InputFormik name={FIELDS.KEY} label={t(T_KEYS.FIELDS.KEY)} spacing='top' required />
                        </Fragment>}
                </Form>
            </FormikProvider>
        </ConfirmModal>
    );
};
